// src/contexts/CartContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';
import { ProductsContext } from './ProductsContext';
import * as LocalStorage from '../utils/localStorage';

// Create the context
export const CartContext = createContext({
  items: [],
  addToCart: () => {},
  removeFromCart: () => {},
  updateQuantity: () => {},
  clearCart: () => {},
  cartSubtotal: 0,
  cartTax: 0,
  estimatedTotal: 0,
  products: [],
  imageItems: [],
  getCoverImage: () => null,
});

const TAX_RATE = 0.0775; // 7.75% default tax rate

// CartContext Provider
export function CartProvider({ children }) {
  const [cart, setCart] = useState({ items: [] });
  const { products, imageItems, getCoverImage } = useContext(ProductsContext);

  // Load cart from localStorage on mount
  useEffect(() => {
    const stored = LocalStorage.getCart();
    setCart(stored);
  }, []);

  const items = cart.items;

  // Add item to cart (or increase quantity if already exists)
  const addToCart = (product, selectedModifiers = [], quantityToAdd = 1) => {
    const existingIndex = items.findIndex(
      item =>
        item.productID === product.id &&
        JSON.stringify(item.selectedModifiers) === JSON.stringify(selectedModifiers)
    );

    let updatedItems;
    if (existingIndex >= 0) {
      // Item exists, increase quantity
      updatedItems = [...items];
      updatedItems[existingIndex] = {
        ...updatedItems[existingIndex],
        quantity: updatedItems[existingIndex].quantity + quantityToAdd,
      };
    } else {
      // New item (minimal structure: only IDs, no denormalized data)
      updatedItems = [
        ...items,
        {
          id: Date.now().toString() + Math.random(),
          productID: product.id,
          quantity: quantityToAdd,
          selectedModifiers: selectedModifiers,
        },
      ];
    }

    const updatedCart = { items: updatedItems };
    LocalStorage.saveCart(updatedCart);
    setCart(updatedCart);
  };

  // Remove item from cart
  const removeFromCart = itemId => {
    const updatedItems = items.filter(item => item.id !== itemId);
    const updatedCart = { items: updatedItems };
    LocalStorage.saveCart(updatedCart);
    setCart(updatedCart);
  };

  // Update item quantity
  const updateQuantity = (itemId, newQuantity) => {
    if (newQuantity < 1) {
      removeFromCart(itemId);
      return;
    }

    const updatedItems = items.map(item =>
      item.id === itemId ? { ...item, quantity: newQuantity } : item
    );

    const updatedCart = { items: updatedItems };
    LocalStorage.saveCart(updatedCart);
    setCart(updatedCart);
  };

  // Clear entire cart
  const clearCart = () => {
    const updatedCart = LocalStorage.clearCart();
    setCart(updatedCart);
  };

  // Calculate cart subtotal by looking up products
  const cartSubtotal = items.reduce((total, item) => {
    const product = products.find(p => p.id === item.productID);
    if (!product) return total;

    // Get base price from priceInfo
    const basePrice = product.priceInfo?.price || 0;

    // Calculate modifiers total
    const modifiersTotal = (item.selectedModifiers || []).reduce((modTotal, mod) => {
      // Find the modifier group and option
      const group = product.modifierGroups?.find(g => g.id === mod.groupID);
      const option = group?.modifierOptions?.find(o => o.id === mod.optionID);
      if (!option) return modTotal;

      return modTotal + (option.priceInfo?.price || 0);
    }, 0);

    // Item total = (base + modifiers) * quantity, all in cents
    return total + ((basePrice + modifiersTotal) * item.quantity);
  }, 0);

  // Calculate tax
  const cartTax = cartSubtotal * TAX_RATE;

  // Calculate estimated total
  const estimatedTotal = cartSubtotal + cartTax;

  const value = {
    items,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    cartSubtotal,
    cartTax,
    estimatedTotal,
    products,
    imageItems,
    getCoverImage,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
